import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Crown, Sparkles, XCircle, CheckCircle2, Loader2 } from "lucide-react";

function money(cents, cur = "usd") {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString("en-US", { style: "currency", currency: cur.toUpperCase() });
}

/**
 * Fan-facing dashboard listing every active membership + unlocked perks.
 * Each membership card shows the show, tier name/price, perks, status, and a
 * "Cancel at period end" action.
 */
export default function Memberships() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/user/memberships");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { if (user?.id) load(); }, [user?.id]);

  if (loading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_banned) return <Navigate to="/banned" replace />;

  const cancel = async (id) => {
    if (!window.confirm("Cancel this membership at the end of the current period? You'll keep access until then.")) return;
    setBusy(id);
    try {
      await api.post(`/user/memberships/${id}/cancel`);
      toast.success("Membership will cancel at period end.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(null); }
  };

  const active = (items || []).filter((m) => ["active", "trialing", "canceling", "past_due"].includes(m.status));
  const inactive = (items || []).filter((m) => m.status === "canceled");

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" data-testid="memberships-page">
      <div className="mb-10 pb-6 border-b border-[var(--ff-border)]">
        <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
          <Crown size={12} /> ▮ MY MEMBERSHIPS
        </div>
        <h1 className="font-display text-4xl lg:text-5xl leading-none" data-testid="memberships-heading">SUPPORTED SHOWS.</h1>
        <p className="text-[var(--ff-text-dim)] text-sm mt-3 font-mono-ui max-w-2xl">
          Every show you&apos;re a monthly member of, plus the perks you&apos;ve unlocked. Cancel any time.
        </p>
      </div>

      {items === null && (
        <div className="text-[var(--ff-text-dim)] text-sm font-mono-ui"><Loader2 className="inline animate-spin" size={14} /> Loading…</div>
      )}

      {items && active.length === 0 && inactive.length === 0 && (
        <div className="surface-card p-12 text-center" data-testid="memberships-empty">
          <Sparkles size={48} className="mx-auto text-[var(--ff-text-dim)] mb-3" />
          <div className="font-display text-2xl mb-2">No memberships yet.</div>
          <div className="text-[var(--ff-text-dim)] text-sm font-mono-ui mb-6">
            Support your favorite hosts — pick a show and a tier to unlock perks.
          </div>
          <Link to="/shows" className="btn-primary" data-testid="memberships-browse-shows">Browse Shows</Link>
        </div>
      )}

      {active.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {active.map((m) => (
            <div key={m.id} className="surface-card p-6" data-testid={`membership-card-${m.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-eyebrow mb-1">{m.tier?.name || "MEMBERSHIP"}</div>
                  <div className="font-display text-2xl">{m.show?.title || "Show"}</div>
                </div>
                <StatusChip status={m.status} />
              </div>
              {m.tier && (
                <>
                  <div className="text-xs font-mono-ui text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mb-3">
                    {money(m.tier.price_cents)} / month
                  </div>
                  <ul className="space-y-1.5 mb-6">
                    {(m.tier.perks || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 size={14} className="text-green-400 mt-0.5 shrink-0" /> <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="pt-4 border-t border-[var(--ff-border)] flex items-center justify-between">
                <Link to={`/shows/${m.show?.id}`} className="btn-ghost text-xs" data-testid={`membership-view-${m.id}`}>
                  View Show
                </Link>
                {m.status !== "canceling" && (
                  <button
                    onClick={() => cancel(m.id)}
                    disabled={busy === m.id}
                    className="btn-ghost text-xs text-[var(--ff-text-dim)] hover:text-white"
                    data-testid={`membership-cancel-${m.id}`}
                  >
                    <XCircle size={12} /> Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {inactive.length > 0 && (
        <>
          <div className="label-eyebrow mb-4 mt-8 text-[var(--ff-text-dim)]">▮ PAST MEMBERSHIPS</div>
          <div className="grid md:grid-cols-2 gap-4 opacity-70">
            {inactive.map((m) => (
              <div key={m.id} className="surface-card p-4 flex items-center justify-between" data-testid={`membership-past-${m.id}`}>
                <div>
                  <div className="font-display text-lg">{m.show?.title}</div>
                  <div className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                    {m.tier?.name} · canceled
                  </div>
                </div>
                <Link to={`/shows/${m.show?.id}`} className="btn-ghost text-xs">Re-subscribe</Link>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active: { text: "ACTIVE", color: "border-green-500/50 text-green-300" },
    trialing: { text: "TRIAL", color: "border-blue-500/50 text-blue-300" },
    canceling: { text: "CANCELING", color: "border-yellow-500/50 text-yellow-300" },
    past_due: { text: "PAST DUE", color: "border-red-500/50 text-red-300" },
    canceled: { text: "CANCELED", color: "border-[var(--ff-border)] text-[var(--ff-text-dim)]" },
  };
  const c = map[status] || map.canceled;
  return (
    <span className={`px-2 py-1 border font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] ${c.color}`} data-testid="membership-status-chip">
      {c.text}
    </span>
  );
}
